import React, { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import type { Market } from '../../types.ts';
import { supabase } from '../../lib/db.ts';
import { patchAdminMarketFees } from '../../lib/api.ts';
import { logger } from '../../services/logger.ts';
import { AdminFeesPanel, defaultFeeConfig, type FeeConfig } from './AdminFeesPanel.tsx';

interface MarketFeesTabProps {
  market: Market;
  marketId: string;
  onMarketUpdate?: () => void | Promise<void>;
}

export function MarketFeesTab({ market, marketId, onMarketUpdate }: MarketFeesTabProps) {
  const [saved, setSaved] = useState<FeeConfig>(defaultFeeConfig);
  const [draft, setDraft] = useState<FeeConfig>(defaultFeeConfig);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const status = (market.status ?? '').toLowerCase();
  const isLocked = status !== 'pending_approval';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadErr(null);
      const { data, error } = await supabase
        .from('markets')
        .select(
          'spread_mode, spread_fixed_rate, spread_dynamic_max_rate, spread_dynamic_min_rate, spread_dynamic_low_probability, spread_dynamic_high_probability, spread_banded_config, performance_fee_enabled, performance_fee_rate, creator_commission_rate, affiliate_commission_rate',
        )
        .eq('id', marketId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setLoadErr('Could not load fees for this market.');
        return;
      }
      const config: FeeConfig = {
        ...defaultFeeConfig,
        ...(data as Record<string, unknown>),
        spread_banded_config:
          Array.isArray(data?.spread_banded_config) && data.spread_banded_config.length > 0
            ? (data.spread_banded_config as FeeConfig['spread_banded_config'])
            : defaultFeeConfig.spread_banded_config,
      } as FeeConfig;
      setSaved(config);
      setDraft(config);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [marketId]);

  const save = async () => {
    setSaving(true);
    setSaveErr(null);
    setSuccessMsg(null);
    try {
      await patchAdminMarketFees({ ...draft, market_id: marketId });
      setSaved(draft);
      setSuccessMsg('Fee override saved for this market.');
      if (onMarketUpdate) await onMarketUpdate();
    } catch (err: unknown) {
      logger.error('Market fee override save error:', err);
      const obj = (err ?? {}) as { message?: unknown };
      const msg = err instanceof Error ? err.message : typeof obj.message === 'string' ? obj.message : '';
      setSaveErr(msg || 'Could not save fees for this market.');
    } finally {
      setSaving(false);
    }
  };

  const lockNotice = isLocked ? (
    <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 text-sm flex items-start gap-2">
      <Lock className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        Fees are locked while this market is <strong>{status || 'unknown'}</strong>. To change the split or spread,
        cancel the market or wait until it is next in <em>pending approval</em>. Platform defaults still apply to
        new markets.
      </div>
    </div>
  ) : (
    <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded p-3 text-sm">
      This market is still <strong>pending approval</strong>. Any changes here override the platform defaults for
      this market only. Once you approve it, these fees are locked in.
    </div>
  );

  return (
    <div className="space-y-4">
      {loadErr && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{loadErr}</div>}
      <AdminFeesPanel
        saved={saved}
        draft={draft}
        onChange={setDraft}
        onSave={save}
        readOnly={isLocked}
        notice={lockNotice}
        saving={saving}
        errorMessage={saveErr}
        successMessage={successMsg}
        saveLabel="Save market override"
        hideMinTradeFloor
      />
    </div>
  );
}
